import React, { useCallback, useState, useEffect } from 'react';
import { Alert, CloseIcon, IconButton, makeStyles } from '@rbx/ui';
import { useRobloxAuthentication } from '@rbx/auth';
import { useTranslation } from '@rbx/intl';
import creatorSettingsClient from '@modules/clients/creatorSettings';
import DynamicallyRouted from '@modules/miscellaneous/common/components/DynamicallyRouted';
import { useAppConfig } from '@modules/layout/layout/components/AppConfigProvider';

const useStyles = makeStyles()((theme) => ({
  alert: {
    borderRadius: 0,
    position: 'sticky',
    top: 0,
    zIndex: theme.zIndex.appBar,
  },
  iaAlert: {
    marginBottom: '12px',
  },
}));

const GUEST_MODE_LOCAL_STORAGE_KEY = 'creator-doc-site-show-min-spec-banner';

export type TMinimumSpecBannerProps = {
  onBannerToggle: (show: boolean) => void;
};

export default function MinimumSpecBanner({ onBannerToggle }: TMinimumSpecBannerProps) {
  // NOTE (tchu, 2025-09-08): commenting this out to turn off the banner until
  // the next update of the minimum specs
  return null;

  const { translateHTML } = useTranslation();
  const { isFetched, user } = useRobloxAuthentication();
  const { enableNewLayout } = useAppConfig();
  const userId = user?.id;

  const [showBanner, setShowBanner] = useState(false);
  const {
    classes: { alert, iaAlert },
  } = useStyles();

  const closeBanner = useCallback(async () => {
    if (isFetched) {
      try {
        // * if user is logged in, get the setting from BE
        if (userId != null) {
          await creatorSettingsClient.updateGenericCreatorSetting(
            userId,
            'GENERIC_CREATOR_SETTING_TYPE_SHOW_MINIMUM_SPEC_BANNER',
            'false',
          );
          // * otherwise enter guest mode, and look in local storage
        } else {
          localStorage.setItem(GUEST_MODE_LOCAL_STORAGE_KEY, 'false');
        }
      } catch {
        // do nothing
      } finally {
        setShowBanner(false);
        onBannerToggle(false);
      }
    }
  }, [isFetched, onBannerToggle, userId]);

  useEffect(() => {
    async function fetchBannerSetting() {
      if (isFetched) {
        // * if user is logged in, get the setting from BE
        try {
          if (userId != null) {
            const { settingValue } = await creatorSettingsClient.getGenericCreatorSetting(
              userId,
              'GENERIC_CREATOR_SETTING_TYPE_SHOW_MINIMUM_SPEC_BANNER',
            );

            // * technically it'd start with '' until we set it to 'false', but treating any
            // * unexpected value as "show the banner"
            if (settingValue !== 'false') {
              setShowBanner(true);
              onBannerToggle(true);
            }

            // * otherwise enter guest mode, and look in local storage
          } else {
            const value = localStorage.getItem(GUEST_MODE_LOCAL_STORAGE_KEY);

            // * similarly here, we treat any value other than "false" as "true"
            if (value !== 'false') {
              setShowBanner(true);
              onBannerToggle(true);
            }
          }
        } catch {
          // do nothing
        }
      }
    }

    fetchBannerSetting();
  }, [isFetched, onBannerToggle, userId]);

  return showBanner ? (
    <Alert
      className={enableNewLayout ? iaAlert : alert}
      severity='info'
      action={
        <IconButton
          aria-label='Close'
          key='iconButton'
          size='small'
          color='inherit'
          onClick={closeBanner}>
          <CloseIcon fontSize='small' />
        </IconButton>
      }>
      {translateHTML('Description.MinSpecBanner', [
        {
          opening: 'linkStart',
          closing: 'linkEnd',
          // eslint-disable-next-line react/no-unstable-nested-components
          content(chunk) {
            return (
              <DynamicallyRouted.Link
                href='/studio/setup#system-requirements'
                color='inherit'
                underline='always'>
                {chunk}
              </DynamicallyRouted.Link>
            );
          },
        },
      ])}
    </Alert>
  ) : null;
}
